"use client";

import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SUPPORTED_LANGUAGES, resolveSupportedLanguage } from "@/app/i18n/languages";
import { safeStorage, I18N_STORAGE_KEY } from "@/lib/safe-storage";

interface LanguageSelectorProps {
  variant?: "default" | "mobile";
  onLanguageChange?: () => void;
  isExpanded?: boolean;
}

export function LanguageSelector({ variant = "default", onLanguageChange, isExpanded }: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = resolveSupportedLanguage(i18n.language);
  const currentLangShort = currentLanguage.shortLabel;

  const handleLanguageChange = (languageCode: string) => {
    // Change the language
    i18n.changeLanguage(languageCode);

    // Store in localStorage to override geolocation detection
    // Note: i18n.changeLanguage() triggers SafeLanguageDetector.cacheUserLanguage(),
    // but we manually save here for immediate persistence in case of errors
    safeStorage.setItem(I18N_STORAGE_KEY, languageCode);

    setIsOpen(false);
    onLanguageChange?.();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (variant === "mobile") {
    return (
      <div className="px-3 py-2" ref={dropdownRef}>
        <div className="text-xs text-white/60 mb-2">Language</div>
        <button
          onClick={() => setIsOpen((v) => !v)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          className="w-full text-left px-3 py-2 text-sm rounded-md flex items-center justify-between bg-white/10 text-white "
        >
          <span className="flex items-center">{currentLanguage.name}</span>
          <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>

        {isOpen && (
          <div className="mt-2 rounded-md overflow-hidden border border-white/10">
            {SUPPORTED_LANGUAGES.map((language) => (
              <button
                key={language.code}
                role="option"
                aria-selected={currentLanguage.code === language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center transition-colors ${
                  currentLanguage.code === language.code
                    ? "text-white  bg-white/10"
                    : "text-white/80 hover:bg-white/10 hover:text-white"
                }`}
              >
                {language.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const isActuallyExpanded = isExpanded ?? true;

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={`h-8 px-2 text-muted-foreground hover:text-foreground border-0 bg-transparent hover:bg-accent/50 w-full flex items-center ${isActuallyExpanded ? 'justify-start' : 'justify-center'} gap-1`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Globe className="h-4 w-4" />
        <span className="hidden sm:inline" title={currentLanguage.name}>{currentLangShort}</span>
        {isActuallyExpanded && (
          <ChevronDown className={`h-3 w-3 ml-0.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        )}
      </Button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 w-full bg-background border border-border rounded-md shadow-lg z-50"
        >
          <div className="py-1">
            {SUPPORTED_LANGUAGES.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground flex items-center transition-colors ${
                  currentLanguage.code === language.code
                    ? "bg-accent text-accent-foreground font-medium"
                    : "text-foreground"
                }`}
              >
                {language.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
